import { PageHero } from "@/components/site/PageHero";
import { SectionLabel, RevealLines, FadeUp } from "@/components/site/motion";
import { Keycaps } from "@/components/about/Keycaps";
import { CTA } from "@/components/site/CTA";
import { TEAM } from "@/lib/content";

const PRINCIPLES = [
  { n: "01", t: "Direct access", b: "You work with the people building your system — not a chain of account managers." },
  { n: "02", t: "End-to-end ownership", b: "Architecture, development, deployment and a structured maintenance period after go-live." },
  { n: "03", t: "Fixed, phased pricing", b: "Each phase is scoped and priced upfront, so there is never a surprise at a milestone." },
  { n: "04", t: "Built to be used", b: "Field teams and central desks alike — we design for the people who live in the tool every day." },
];

const Story = () => (
  <section data-testid="about-story" className="py-24 md:py-36">
    <div className="mx-auto grid max-w-[1440px] gap-12 px-6 md:px-10 lg:grid-cols-12">
      <div className="lg:col-span-4"><SectionLabel n="(01)">Our story</SectionLabel></div>
      <div className="lg:col-span-8">
        <FadeUp as="p" className="font-display text-3xl font-medium leading-[1.15] tracking-[-0.02em] text-cream md:text-5xl">
          Skaris Technology builds custom software, web platforms and management portals for enterprise and infrastructure-sector clients.
        </FadeUp>
        <div className="mt-12 max-w-3xl font-body text-base leading-relaxed text-cream/75">
          <FadeUp delay={0.1}><p>We work directly with clients from requirements through deployment and post-launch support — without the overhead of a large agency. Today that includes IT services, digital transformation and AI transformation programmes.</p></FadeUp>
        </div>
      </div>
    </div>
  </section>
);

const Principles = () => (
  <section data-testid="about-principles" className="border-y border-cream/10 py-24 md:py-32">
    <div className="mx-auto max-w-[1440px] px-6 md:px-10">
      <SectionLabel n="(02)">How we work</SectionLabel>
      <RevealLines inView as="h2" lines={["Lean by design.", "Accountable by default."]}
        className="mt-6 font-display text-4xl font-semibold uppercase leading-[0.95] tracking-[-0.03em] text-cream sm:text-5xl lg:text-7xl"
        lineClass={(i) => (i === 1 ? "text-gradient" : "")} />
      <div className="mt-16 divide-y divide-cream/10 border-t border-cream/10">
        {PRINCIPLES.map((p, i) => (
          <FadeUp key={p.n} delay={i * 0.05} data-testid={`principle-${p.n}`} className="group grid gap-4 py-8 transition-colors duration-500 hover:bg-skaris-navy/25 md:grid-cols-12 md:items-center md:px-4">
            <span className="font-mono text-xs text-skaris-sky md:col-span-1">{p.n}</span>
            <h3 className="font-display text-2xl font-semibold text-cream transition-transform duration-500 group-hover:translate-x-2 md:col-span-5 md:text-4xl">{p.t}</h3>
            <p className="font-body text-sm text-cream/75 md:col-span-5 md:col-start-8 md:text-base">{p.b}</p>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

const Values = () => (
  <section data-testid="about-values" className="py-24 md:py-32">
    <div className="mx-auto grid max-w-[1440px] gap-12 px-6 md:px-10 lg:grid-cols-12">
      <div className="lg:col-span-4">
        <SectionLabel n="(03)">Values</SectionLabel>
        <RevealLines inView as="h2" lines={["What we", "stand on."]}
          className="mt-6 font-display text-4xl font-semibold uppercase leading-[0.95] tracking-[-0.03em] text-cream sm:text-5xl" />
        <FadeUp as="p" className="mt-6 max-w-sm font-body text-base text-cream/75">Eight principles that shape every decision — from the first architecture review to the last support ticket.</FadeUp>
      </div>
      <div className="[perspective:1000px] lg:col-span-8"><Keycaps /></div>
    </div>
  </section>
);

const Team = () => (
  <section data-testid="about-team" className="pb-24 md:pb-32">
    <div className="mx-auto max-w-[1440px] px-6 md:px-10">
      <SectionLabel n="(04)">The team</SectionLabel>
      <div className="mt-12 grid grid-cols-1 border-y border-cream/10 sm:grid-cols-2 lg:grid-cols-4">
        {TEAM.map((m, i) => (
          <FadeUp key={i} delay={i * 0.08} data-testid={`team-member-${i}`}
            className={`group py-8 transition-colors duration-500 hover:bg-skaris-navy/25 sm:px-6 ${i > 0 ? "border-t border-cream/10 sm:border-t-0" : ""} ${i % 2 ? "sm:border-l sm:border-cream/10" : ""} ${i > 1 ? "sm:border-t lg:border-t-0" : ""} ${i === 2 ? "lg:border-l lg:border-cream/10" : ""}`}>
            <span className="font-mono text-[11px] text-skaris-sky">0{i + 1}</span>
            <p className="mt-6 font-display text-xl font-semibold text-cream transition-transform duration-500 group-hover:translate-x-1">{m.name}</p>
            <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-cream/60">{m.role}</p>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

export default function About() {
  return (
    <main data-testid="about-page">
      <PageHero
        testid="about-hero"
        label="About Skaris"
        lines={["Lean team.", "Serious", "delivery."]}
        intro="A delivery-focused IT services firm that takes projects end-to-end — and stays accountable long after go-live."
        img="/img/about-team.jpg"
      />
      <Story />
      <Principles />
      <Values />
      <Team />
      <CTA />
    </main>
  );
}
